/* global AbortController, RequestInit, Response, fetch */
import { getLoggingConfig } from "./logging";
function buildCheckpointUrl(sessionId, checkpointId) {
    const config = getLoggingConfig();
    const base = `${config.serverUrl}/api/sessions/${sessionId}/checkpoints`;
    return checkpointId ? `${base}/${checkpointId}` : base;
}
export async function resolveAuthHeaderOrThrow(options) {
    let authHeader = options.getAuthHeader();
    if (!authHeader && options.initialize) {
        await options.initialize();
        authHeader = options.getAuthHeader();
    }
    if (!authHeader) {
        throw new Error(options.unauthenticatedMessage || "Not authenticated. Please log in and retry.");
    }
    return authHeader;
}
async function fetchWithOptionalTimeout(url, init, timeoutMs, label) {
    if (!timeoutMs || timeoutMs <= 0) {
        return fetch(url, init);
    }
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...(init || {}), signal: controller.signal });
    }
    catch (error) {
        if (error.name === "AbortError") {
            throw new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`);
        }
        throw error;
    }
    finally {
        globalThis.clearTimeout(timeoutId);
    }
}
export function createCheckpointApiClient(options = {}) {
    const defaultTimeoutMs = options.defaultTimeoutMs;
    async function fetchWithTimeout(url, init, timeoutMs, label) {
        return fetchWithOptionalTimeout(url, init, timeoutMs, label);
    }
    async function listCheckpoints(sessionId, timeoutMs = defaultTimeoutMs) {
        const response = await fetchWithOptionalTimeout(buildCheckpointUrl(sessionId), undefined, timeoutMs, `List checkpoints (${sessionId})`);
        if (!response.ok) {
            throw new Error(`Failed to list checkpoints: ${response.status} ${response.statusText}`);
        }
        const payload = (await response.json());
        return payload.data?.checkpoints || [];
    }
    async function getCheckpoint(sessionId, checkpointId, timeoutMs = defaultTimeoutMs) {
        const response = await fetchWithOptionalTimeout(buildCheckpointUrl(sessionId, checkpointId), undefined, timeoutMs, `Get checkpoint (${checkpointId})`);
        if (!response.ok) {
            throw new Error(`Failed to fetch checkpoint: ${response.status} ${response.statusText}`);
        }
        const payload = (await response.json());
        const checkpoint = payload.data;
        if (!checkpoint) {
            throw new Error("Checkpoint payload missing data.");
        }
        return checkpoint;
    }
    async function createCheckpoint(sessionId, payload, timeoutMs = defaultTimeoutMs) {
        return fetchWithOptionalTimeout(buildCheckpointUrl(sessionId), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }, timeoutMs, `Create checkpoint (${sessionId})`);
    }
    async function deleteCheckpoint(sessionId, checkpointId, timeoutMs = defaultTimeoutMs) {
        return fetchWithOptionalTimeout(buildCheckpointUrl(sessionId, checkpointId), { method: "DELETE" }, timeoutMs, `Delete checkpoint (${checkpointId})`);
    }
    return {
        fetchWithTimeout,
        listCheckpoints,
        getCheckpoint,
        createCheckpoint,
        deleteCheckpoint,
    };
}
